import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import {
  ArrowRight,
  Check,
  FileUp,
  Sparkles,
  Upload,
  Wand2,
} from "lucide-react";

type RiskAnalysisModalProps = {
  isOpen: boolean;
  onClose: () => void;
  onChooseWizard: () => void;
  onChooseUpload: () => void;
};

export default function RiskAnalysisModal({
  isOpen,
  onClose,
  onChooseWizard,
  onChooseUpload,
}: RiskAnalysisModalProps) {
  return (
    <Dialog open={isOpen} onOpenChange={onClose}>
      <DialogContent className="sm:max-w-[600px] p-4 md:p-8">
        <DialogHeader className="text-center mb-2 mt-4">
          <div className="mx-auto w-14 h-14 rounded-full bg-blue-100 hidden md:flex items-center justify-center mb-3">
            <Sparkles className="w-7 h-7 text-blue-600" />
          </div>
          <DialogTitle className="text-xl md:text-2xl font-bold text-slate-800 text-center">
            Cum dorești să începi analiza de risc?
          </DialogTitle>
          <DialogDescription className="text-sm text-slate-500 mt-2 max-w-md mx-auto">
            Alege metoda care ți se potrivește. Poți oricând reveni și schimba.
          </DialogDescription>
        </DialogHeader>

        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
          <button
            onClick={onChooseWizard}
            className="relative flex flex-col items-start md:items-center text-left md:text-center border-2 border-blue-500 rounded-xl p-5 md:p-6 gap-3 bg-blue-50/30 shadow-md hover:shadow-lg hover:scale-[1.02] hover:border-blue-600 transition-all duration-300 ease-out animate-in fade-in slide-in-from-bottom-2 fill-mode-both"
            style={{ animationDelay: "100ms", animationDuration: "400ms" }}
          >
            <div className="absolute -top-3 left-1/2 -translate-x-1/2 bg-blue-600 text-white text-xs font-bold px-3 py-1 rounded-full shadow-sm">
              RECOMANDAT
            </div>

            <div className="flex flex-row md:flex-col items-center gap-3 w-full md:w-auto">
              <div className="w-10 h-10 md:w-14 md:h-14 rounded-full bg-gradient-to-br from-blue-500 to-blue-700 flex items-center justify-center shadow-md shrink-0">
                <Wand2 className="w-5 h-5 md:w-7 md:h-7 text-white" />
              </div>

              <h3 className="font-bold text-slate-800 text-base text-left md:text-center">
                Completează automat
              </h3>
            </div>

            <p className="text-xs text-slate-500 leading-relaxed text-left md:text-center">
              Parcurge wizardul pas cu pas. Selectează sectoare și ajustează
              matricile de risc.
            </p>

            <div className="space-y-1.5 text-left w-full">
              <div className="flex items-center gap-2 text-xs text-slate-600">
                <Check className="w-3.5 h-3.5 text-blue-500 shrink-0" />
                <span>Selectare sectoare NIS2</span>
              </div>
              <div className="flex items-center gap-2 text-xs text-slate-600">
                <Check className="w-3.5 h-3.5 text-blue-500 shrink-0" />
                <span>Editare matrice de risc</span>
              </div>
              <div className="flex items-center gap-2 text-xs text-slate-600">
                <Check className="w-3.5 h-3.5 text-blue-500 shrink-0" />
                <span>Generare raport PDF</span>
              </div>
            </div>

            <div className="w-full pt-2 mt-auto">
              <Button className="w-full !text-xs" size="sm">
                Începe analiza
                <ArrowRight className="w-4 h-4" />
              </Button>
            </div>
          </button>

          <button
            onClick={onChooseUpload}
            className="flex flex-col items-start md:items-center text-left md:text-center border border-slate-200 rounded-xl p-5 md:p-6 gap-3 bg-white hover:shadow-md hover:scale-[1.02] hover:border-slate-300 transition-all duration-300 ease-out animate-in fade-in slide-in-from-bottom-2 fill-mode-both"
            style={{ animationDelay: "200ms", animationDuration: "400ms" }}
          >
            <div className="flex flex-row md:flex-col items-center gap-3 w-full md:w-auto">
              <div className="w-10 h-10 md:w-14 md:h-14 rounded-full bg-emerald-50 flex items-center justify-center shrink-0">
                <FileUp className="w-5 h-5 md:w-7 md:h-7 text-emerald-600" />
              </div>

              <h3 className="font-semibold text-slate-700 text-base text-left md:text-center">
                Încarcă Formular XLSX
              </h3>
            </div>

            <p className="text-xs text-slate-500 leading-relaxed text-left md:text-center">
              Ai deja analiza de risc completată? Extrage automat datele din
              fișierul XLSX.
            </p>

            <div className="space-y-1.5 text-left w-full">
              <div className="flex items-center gap-2 text-xs text-slate-500">
                <Check className="w-3.5 h-3.5 text-emerald-500 shrink-0" />
                <span>Extragere automată din XLSX</span>
              </div>
              <div className="flex items-center gap-2 text-xs text-slate-500">
                <Check className="w-3.5 h-3.5 text-emerald-500 shrink-0" />
                <span>Verificare și ajustare ulterioară</span>
              </div>
              <div className="flex items-center gap-2 text-xs text-slate-500">
                <Check className="w-3.5 h-3.5 text-emerald-500 shrink-0" />
                <span>Salvare directă sau editare</span>
              </div>
            </div>

            <div className="w-full pt-2 mt-auto">
              <Button variant="secondary" className="w-full !text-xs" size="sm">
                Încarcă document
                <Upload className="w-4 h-4" />
              </Button>
            </div>
          </button>
        </div>

        <p className="text-xs text-slate-400 text-center mt-4">
          Poți schimba metoda oricând. Datele se salvează automat.
        </p>
      </DialogContent>
    </Dialog>
  );
}
